"use client";

import React from "react";
import { Sparkles, RefreshCw, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Link } from "@/components/common/Link";
import { WP_Term_Query } from "@/integrations/wordpress/wp_term_query";
import {
  EcommerceTermsProvider,
  useEcommerceTerms,
} from "@/integrations/wordpress/WordPressEcommerceTermsProvider";
import {
  ShopBreadcrumbsProvider,
  useShopBreadcrumbs,
} from "@/integrations/wordpress/WordPressEcommerceShopBreadcrumbs";

declare const wvcClient: any;

function BreadcrumbsConsumer() {
  const { links, loading, error } = useShopBreadcrumbs();

  if (loading || error || !links || links.length === 0) {
    return (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem data-index={0}>
            <BreadcrumbLink asChild>
              <Link to="/HomePage">Accueil</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem data-index={1}>
            <BreadcrumbPage>Boutique</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    );
  }

  const lastIndex = links.length - 1;

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {links.map((link, i) => (
          <React.Fragment key={link.href || i}>
            <BreadcrumbItem data-index={i}>
              {i === lastIndex ? (
                <BreadcrumbPage>{link.title}</BreadcrumbPage>
              ) : (
                <BreadcrumbLink asChild>
                  <Link to={link.href || "#"}>{link.title}</Link>
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
            {i < lastIndex && <BreadcrumbSeparator />}
          </React.Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

function CategoryListConsumer() {
  const { terms, loading, error, refetch } = useEcommerceTerms();

  if (loading) {
    return (
      <div className="mt-6 flex flex-wrap gap-2.5">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            data-index={i}
            className="h-9 w-28 animate-pulse rounded-full bg-muted/70"
          />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-6">
        <Alert variant="destructive" className="max-w-md">
          <AlertTitle>Erreur de chargement</AlertTitle>
          <AlertDescription className="mt-2 flex items-center justify-between gap-4">
            <span>Impossible d'afficher les catégories.</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              className="gap-1.5"
            >
              <RefreshCw className="size-3.5" />
              Réessayer
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!terms || terms.length === 0) {
    return null;
  }

  return (
    <div className="mt-8">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Nos catégories
      </h2>
      <div className="mt-3 flex flex-wrap items-center gap-2 md:gap-3">
        {terms.map((term, i) => (
          <div key={term.id || i} data-index={i}>
            <Link
              to={term.link || "#"}
              className="group inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-xs font-medium text-card-foreground shadow-xs transition-all hover:border-primary hover:bg-primary hover:text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {term.image_url ? (
                <img
                  src={term.image_url}
                  alt={term.name}
                  className="size-4 rounded-full object-cover"
                  loading="lazy"
                />
              ) : (
                <ShoppingBag className="size-3.5 text-muted-foreground group-hover:text-primary-foreground" />
              )}
              <span>{term.name}</span>
              {typeof term.count === "number" && (
                <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground group-hover:bg-primary-foreground/20 group-hover:text-primary-foreground">
                  {term.count}
                </span>
              )}
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Archive() {
  const queryParams =
    typeof wvcClient !== "undefined"
      ? wvcClient.getCurrentWPTermQueryParams()
      : { taxonomy: "product_cat", per_page: 12 };
  const wp_term_query = new WP_Term_Query(queryParams);

  return (
    <section data-section-id="41187"
      id="archive-title-section"
      data-nav="light"
      className="relative w-full border-b border-border bg-background py-10 text-foreground md:py-14"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="mb-6">
          <ShopBreadcrumbsProvider>
            <BreadcrumbsConsumer />
          </ShopBreadcrumbsProvider>
        </div>

        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3.5 py-1 text-xs font-medium text-primary mb-4">
            <Sparkles className="size-3.5" />
            <span>Sélection du Sommelier</span>
          </div>

          <h1 className="font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            Vins, Spiritueux & Épicerie
          </h1>

          <p className="mt-4 text-base text-muted-foreground sm:text-lg leading-relaxed">
            Découvrez notre sélection de vins de France, whiskies artisanaux et produits d'épicerie fine, choisis avec soin par votre sommelier-conseil.
          </p>

          <EcommerceTermsProvider wp_term_query={wp_term_query}>
            <CategoryListConsumer />
          </EcommerceTermsProvider>
        </div>
      </div>
    </section>
  );
}